import React, { useEffect, useState } from 'react'
import { ComposedChart, Line, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts'
import { Card } from '../ui/Card'
import { LoadingSpinner } from '../LoadingSpinner'
import { API_BASE_URL } from '../../data'

type MetricDataPoint = {
    timestamp: string
    value: number
}

type Props = {
    metricKeys: string[]
    title: string
    description?: string
    days?: number
    aggregation?: 'none' | 'hourly' | 'daily'
    lineColor?: string
    valueFormatter?: (value: number) => string
    height?: number
}

export const AggregatedMetricLineChart = ({
    metricKeys,
    title,
    description,
    days = 3,
    aggregation = 'hourly',
    lineColor = '#3b82f6',
    valueFormatter = (value) => value.toLocaleString(),
    height = 300
}: Props) => {
    const [data, setData] = useState<MetricDataPoint[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [currentValue, setCurrentValue] = useState<number | null>(null)
    const [isHovering, setIsHovering] = useState(false)

    useEffect(() => {
        const fetchMetricHistory = async () => {
            setLoading(true)
            setError(null)

            try {
                const endDate = new Date()
                const startDate = new Date()
                startDate.setDate(startDate.getDate() - days)

                // Fetch all metrics in batch
                const batchResponse = await fetch(
                    `${API_BASE_URL}/metrics/batch`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        credentials: 'include',
                        body: JSON.stringify({
                            metrics: metricKeys,
                            start_date: startDate.toISOString(),
                            end_date: endDate.toISOString(),
                            aggregation: aggregation
                        })
                    }
                )

                if (!batchResponse.ok) {
                    throw new Error(`Failed to fetch metrics: ${batchResponse.status}`)
                }

                const batchResult = await batchResponse.json()
                const metricsData = batchResult.data || {}

                // Aggregate data across all metrics by timestamp
                const timestampMap: Record<string, number> = {}

                metricKeys.forEach(metricKey => {
                    const metricData = metricsData[metricKey] || []
                    metricData.forEach((point: any) => {
                        const ts = point.timestamp
                        const value = aggregation === 'none' ? point.value : point.avg_value
                        timestampMap[ts] = (timestampMap[ts] || 0) + value
                    })
                })

                // Convert to array and sort by timestamp
                const aggregatedData = Object.entries(timestampMap)
                    .map(([timestamp, value]) => ({ timestamp, value }))
                    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())

                setData(aggregatedData)

                // Calculate current value as sum of latest values
                let currentSum = 0
                const latestPromises = metricKeys.map(async (metricKey) => {
                    try {
                        const response = await fetch(
                            `${API_BASE_URL}/metrics/${metricKey}/latest`,
                            { credentials: 'include' }
                        )
                        if (response.ok) {
                            const result = await response.json()
                            return result.latest?.value || 0
                        }
                    } catch (err) {
                        return 0
                    }
                    return 0
                })

                const latestValues = await Promise.all(latestPromises)
                currentSum = latestValues.reduce((sum, val) => sum + val, 0)
                setCurrentValue(currentSum)

            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to load metric data')
            } finally {
                setLoading(false)
            }
        }

        fetchMetricHistory()
    }, [metricKeys, days, aggregation])

    const formatXAxis = (timestamp: string) => {
        const date = new Date(timestamp)
        if (aggregation === 'daily') {
            return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
        }
        return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit' })
    }

    const formatTooltip = (value: number) => {
        return valueFormatter(value)
    }

    const CustomTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                        {new Date(label).toLocaleString()}
                    </p>
                    <p className="text-sm font-semibold" style={{ color: lineColor }}>
                        {valueFormatter(payload[0].value)}
                    </p>
                </div>
            )
        }
        return null
    }

    if (loading) {
        return (
            <Card className="p-6">
                <LoadingSpinner meta={{ loadingText: 'Loading metric data...' }} />
            </Card>
        )
    }

    if (error) {
        return (
            <Card className="p-6">
                <div className="text-danger-600 dark:text-danger-400">
                    <p className="font-semibold">Error</p>
                    <p className="text-sm">{error}</p>
                </div>
            </Card>
        )
    }

    return (
        <Card className="p-6 relative">
            <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {title}
                </h3>
                {description && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {description}
                    </p>
                )}
            </div>

            <div className="relative">
                {/* Large number overlay */}
                {currentValue !== null && (
                    <div
                        className="absolute inset-0 flex items-center justify-center pointer-events-none z-10 transition-opacity duration-300"
                        style={{ opacity: isHovering ? 0 : 0.9 }}
                    >
                        <div className="text-6xl font-bold" style={{ color: lineColor }}>
                            {valueFormatter(currentValue)}
                        </div>
                    </div>
                )}

                {/* Line chart */}
                <ResponsiveContainer width="100%" height={height}>
                    <ComposedChart
                        data={data}
                        onMouseEnter={() => setIsHovering(true)}
                        onMouseLeave={() => setIsHovering(false)}
                    >
                        <defs>
                            <linearGradient id={`gradient-${metricKeys.join('-')}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={lineColor} stopOpacity={0.3}/>
                                <stop offset="95%" stopColor={lineColor} stopOpacity={0.05}/>
                            </linearGradient>
                        </defs>
                        <XAxis dataKey="timestamp" hide />
                        <YAxis hide />
                        <Tooltip content={<CustomTooltip />} />
                        <Area
                            type="monotone"
                            dataKey="value"
                            stroke="none"
                            fill={`url(#gradient-${metricKeys.join('-')})`}
                            fillOpacity={1}
                        />
                        <Line
                            type="monotone"
                            dataKey="value"
                            stroke={lineColor}
                            strokeWidth={2}
                            dot={false}
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>

            <div className="mt-4 text-xs text-gray-500 dark:text-gray-400 text-center">
                Last {days} days
            </div>
        </Card>
    )
}
